/**
 * Access policies for different roles.
 * "default" role used as a fallback.
 *
 * Each role should have an array of access rules.
 * The rule is on the form of:
 *   + 'namespace:action' - permit 'action' in 'namespace', for instance: 'accounts:read'
 *
 * Convention for actions:
 *   + namespace is reflection of resource or the page (dependent on the context)
 *   + the basic set of actions is: read, create, update, destroy
 *   + there can be custom actions if required
 *
 * More info, including a list of actions and views can be found here: https://miro.com/app/board/uXjVIaDxu3c=/
 */

/* Full list of actions:
    'accept_funding_update',
    'add_user',
    'add_attachment',
    'approve_batch',
    'approve_payout_draft',
    'cancel_order',
    'create_batch_template',
    'create_batch',
    'create_payout',
    'delete_payout_draft',
    'create_receiver',
    'delete_receiver',
    'delete_attachment',
    'download_payout',
    'download_statement',
    'download_reports',
    'download_attachment',
    'export_payouts',
    'export_transaction',
    'inter-account-transfer',
    'reject_payout_draft',
    'reject_batch',
    'refund_order',
    'update_user',
    'update_other_user',
    'withdraw_funds'
*/

type ActionNamespace =
  | 'accounts'
  | 'attachments'
  | 'collections'
  | 'collection_orders'
  | 'configurations'
  | 'crypto'
  | 'developers'
  | 'exports'
  | 'payouts'
  | 'payout_batches'
  | 'payout_drafts'
  | 'receivers'
  | 'users'
  | 'withdrawals'
  | 'dashboard';

type Action =
  | 'manage'
  | 'read'
  | 'review'
  | 'funding'
  | 'accept_funding_update'
  | 'download';

type PermitAction = `${ActionNamespace}:${Action}`;
type Policies = Record<string, PermitAction[]>;

const base = [
  'accounts:read',
  'collections:read',
  'collection_orders:read',
  'dashboard:read',
  'payouts:read',
  'payout_batches:read',
  'payout_drafts:read',
  'receivers:read',
  'withdrawals:read'
] satisfies PermitAction[];

const exports = [
  'exports:read',
  'exports:manage',
  'exports:download'
] satisfies PermitAction[];

const manage = [
  'accounts:manage',
  'attachments:manage',
  'accounts:funding',
  'accounts:accept_funding_update',
  'collection_orders:manage',
  'payouts:manage',
  'payout_batches:manage',
  'payout_drafts:manage',
  'receivers:manage',
  'withdrawals:manage'
] satisfies PermitAction[];

const reviewer = [
  'payout_batches:review',
  'payout_drafts:review'
] satisfies PermitAction[];

const users = ['users:read', 'users:manage'] satisfies PermitAction[];

const developers = ['developers:manage'] satisfies PermitAction[];

const configurations = [
  'configurations:read',
  'configurations:manage'
] satisfies PermitAction[];

export const policies = {
  default: ['dashboard:read'],
  observer: [...base],
  developer: [...base, ...developers, ...configurations],
  finance: [...base, ...exports, 'accounts:funding'],
  'operations member': [...base, ...exports, ...manage, 'crypto:manage'],
  operations_member_reviewer: [...base, ...exports, ...manage, ...reviewer],
  admin: [
    'crypto:manage',
    ...base,
    ...exports,
    ...manage,
    ...users,
    ...developers,
    ...configurations
  ],
  admin_reviewer: [
    ...base,
    ...exports,
    ...manage,
    ...reviewer,
    ...users,
    ...developers,
    ...configurations
  ]
} satisfies Policies;
